<template>
    <ai-form-text-box-dictionary
        ref="aiFormTextBoxDictionaryRef"
        v-model:value="value"
        :fieldNameText="meta.fieldNameText"
        :fieldNameId="meta.fieldNameId"
        :title="meta.title"
        :display-expr="meta.extParams.displayExpr"
        :value-expr="meta.extParams.valueExpr"
        :dataSource="displayOkvedList"
        @text-box-real-value-changed="textBoxRealValueChanged"
        @popup-shown="popupShown"
    >
        <div class="h-full flex flex-col">
            <div class="h-[550px]">
                <ai-dictionary-grid
                    :data-source="displayOkvedResolutionList"
                    :meta="metaResolution.extParams.metaDictionary"
                    :toolbar-items="toolbarItems"
                    :use-default-toolbar="false"
                    @dictionary-selection-changed="gridSelectionChanged"
                />
            </div>
            <div class="h-full">
                <ai-dictionary-tree-list
                    ref="treeListRef"
                    :data-source="displayOkvedList"
                    :meta="metaOkved.extParams.metaDictionary"
                    :use-default-toolbar="false"
                    :allow-select-parent-nodes="allowSelectParentNodes"
                    @dictionary-selection-changed="treeListSelectionChanged"
                    @dictionary-focus-changed="treeListFocusChanged"
                    @userDataChanged="userDataChanged"
                />
            </div>
        </div>
    </ai-form-text-box-dictionary>
</template>

<script setup>
import AiFormTextBoxDictionary from '@/components/card-component/ai-form-text-box-dictionary.vue';
import AiDictionaryGrid from '@/components/dictionary-components/ai-dictionary-grid.vue';
import AiDictionaryTreeList from '@/components/dictionary-components/ai-dictionary-tree-list.vue';
import { getOkvedResolution } from '@/service/counteragent-service';
import { getOkved } from '@/service/counteragent-service';

import { ref, computed, inject, reactive } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps({
    meta: {
        type: Object,
        default: () => {},
    },
    callbacks: {
        type: Object,
        default: () => ({ userDataChanged: null }),
    },
});

const allowSelectParentNodes = false;
const userData = inject('userData');

const state = reactive({
    okvedResolutionList: [],
    okvedList: [],
});

const refresh = async () => {
    state.okvedResolutionList = await getOkvedResolution();
    state.okvedList = await getOkved();
};

refresh();

const value = computed({
    get: () => {
        if (userData.value[props.meta.fieldNameText] === undefined) {
            return {result: true, items : []};
        }
        return userData.value[props.meta.fieldNameText];
    },
});

const userDataChanged = () => {
    props.callbacks.userDataChanged();
};

// GRID OKVED RESOLUTION LOGIC

const displayOkvedResolutionList = computed(() => state.okvedResolutionList);

const selectedResolution = ref(1);

const gridSelectionChanged = event => {
    selectedResolution.value = event.kod_res_okved;
};

// TREE LIST OKVED LOGIC

const displayOkvedList = computed(() => {
    if (state.okvedList)
        return state.okvedList.filter(o => o.kod_res_okved === selectedResolution.value);
    else
        return {result: true, items : []};
});

const treeListRef = ref(null);
const aiFormTextBoxDictionaryRef = ref(null);

const focusedRowKey = ref(null);

const textBoxRealValueChanged = newValue => {
    focusedRowKey.value = newValue;
};

const popupShown = () => {
    console.log('component-kod-okved', treeListRef.value.treeListInstance)
    treeListRef.value?.collapseAll();
    treeListRef.value.treeListInstance?.refresh();
    treeListRef.value?.treeListInstance?.option('focusedRowKey', focusedRowKey.value);
};

const treeListSelectionChanged = event => {
    userData.value[props.meta.fieldNameText] = event.data[props.meta.extParams.displayExpr];
    userDataChanged();
    aiFormTextBoxDictionaryRef.value?.popupInstance.hide();
};

const focusedRow = ref(null);

const treeListFocusChanged = event => {
    if (allowSelectParentNodes || !focusedRow.value?.node.hasChildren) {
        focusedRow.value = event;
    }
};

const selectRowOnClick = () => {
    treeListSelectionChanged({ key: focusedRow.value?.key, data: focusedRow.value?.data });
};

const router = useRouter();

const openNsiDictOnClick = () => {
    router.push({ name: 'NsiView', query: { name: 'ks_okved', id: '50140' } });
};

const toolbarItems = [
    {
        widget: 'dxButton',
        location: 'before',
        showText: 'inMenu',
        locateInMenu: 'auto',
        options: {
            icon: 'refresh',
            hint: 'Обновить',
            text: 'Обновить',
            onClick: refresh,
        },
    },
    {
        widget: 'dxButton',
        location: 'before',
        showText: 'inMenu',
        locateInMenu: 'auto',
        options: {
            icon: 'edit',
            hint: 'Редактировать в НСИ',
            text: 'Редактировать в НСИ',
            onClick: openNsiDictOnClick,
        },
    },
    {
        widget: 'dxButton',
        location: 'before',
        showText: 'inMenu',
        locateInMenu: 'auto',
        options: {
            icon: 'check',
            hint: 'Выбрать',
            text: 'Выбрать',
            onClick: selectRowOnClick,
        },
    },
];

const metaResolution = {
    fieldType: 'ai-dictionary-grid',
    size: [1, 4],
    fieldNameText: 'kod_res_okved',
    extParams: {
        displayExpr: 'res_name',
        valueExpr: 'kod_res_okved',
        metaDictionary: {
            label: 'Версия ОКВЭД',
            keyExpr: 'kod_res_okved',
            focusedRowEnabled: true,
            focusedRowKey: selectedResolution.value,
            rootValue: 0,
            columns: [
                {
                    dataField: 'res_name',
                    caption: 'Редакция справочника',
                    width: '150px',
                },
                {
                    dataField: 'res_date_from',
                    caption: 'Дата начала действия',
                    dataType: 'date',
                    width: '150px',
                },
                {
                    dataField: 'res_date_to',
                    caption: 'Дата окончания действия',
                    dataType: 'date',
                    width: '150px',
                },
                {
                    dataField: 'res_remark',
                    caption: 'Примечание',
                },
            ],
        },
    },
};

const metaOkved = {
    fieldType: 'ai-dictionary-tree-list',
    size: [1, 6],
    fieldNameText: 'kod_okved',
    extParams: {
        displayExpr: 'okved',
        valueExpr: 'kod_okved',
        metaDictionary: {
            label: 'ОКВЭД',
            keyExpr: 'kod_okved',
            parentIdExpr: 'parent_kod_okved',
            focusedRowEnabled: true,
            columns: [
                {
                    dataField: 'okved',
                    caption: 'Код ОКВЕД',
                    width: '250px',
                },
                {
                    dataField: 'name',
                    caption: 'Наименование',
                },
            ],
        },
    },
};
</script>
